/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '@ion/applications/src/store/helpers';
import {PageState} from '@ion/applications/src/types/pages';
import {gridDefaultPageSize} from '@ion/applications/src/utils/helpers';
import {
  Application,
  ApplicationFilters,
  Applications
} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {AuditRecordType} from '@ion/dls/src/editors/audit/AuditGridUtils';
import {AdminAction, AsyncState} from '@/types/redux';
import {
  ADD_ADMIN_APPLICATION_ERROR,
  ADD_ADMIN_APPLICATION_REQUEST,
  ADD_ADMIN_APPLICATION_RESPONSE,
  ADMIN_APPLICATIONS_GRID_REFRESHED,
  AUDIT_ADMIN_APPLICATION,
  AUDIT_ADMIN_APPLICATION_SHOW_CHANGES_DIALOG,
  DELETE_ADMIN_APPLICATIONS_ERROR,
  DELETE_ADMIN_APPLICATIONS_REQUEST,
  DELETE_ADMIN_APPLICATIONS_RESPONSE,
  DISPLAY_DELETE_APPLICATIONS_DIALOG,
  EDIT_ADMIN_APPLICATION_ERROR,
  EDIT_ADMIN_APPLICATION_REQUEST,
  EDIT_ADMIN_APPLICATION_RESPONSE,
  GET_ADMIN_APPLICATIONS_ERROR,
  GET_ADMIN_APPLICATIONS_REQUEST,
  GET_ADMIN_APPLICATIONS_RESPONSE,
  INITIALIZE_ADMIN_APPLICATIONS,
  RESET_APPLICATIONS_SEARCH_QUERIES,
  SET_GRID_COLUMNS,
  SET_GRID_PAGE_SIZE,
  SET_VIEW_SUB_GROUPS,
  SHOW_ADD_APPLICATION_DIALOG,
  SHOW_EDIT_APPLICATION_DIALOG,
  UPDATE_ADMIN_APPLICATIONS_GRID_COLUMNS,
  UPDATE_APPLICATIONS_SEARCH_QUERIES
} from '../actions/admin-applications';

export interface AdminApplicationsAction extends AdminAction {
  applications?: Application | Applications;
  showDialog?: boolean;
  isView?: boolean;
  searchParams?: ApplicationFilters;
};

export interface ApplicationAuditState extends ApplicationActionState {
  showChangesDialog?: boolean;
  auditRecord?: AuditRecordType;
};

interface ApplicationActionState extends AsyncState {
  selected?: Application | Applications | null;
  result?: Application | Applications | null;
  isView?: boolean;
  showDialog?: boolean;
};

export interface AdminApplicationsState extends PageState {
  applications: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    includeSubGroups: boolean,
    pageSize: number
  },
  search: ApplicationFilters;
  add: ApplicationActionState;
  destroy: ApplicationActionState;
  edit: ApplicationActionState;
  audit: ApplicationAuditState;
};

//defines the user preference key for the admin applications page ...
export const PREFERENCE = 'admin-applications';

//defines the initial state for the reducer ...
export const initialState: AdminApplicationsState = {
  isInitialized : false,
  applications  : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    includeSubGroups    : false,
    pageSize            : gridDefaultPageSize
  },
  search : {
    name           : null,
    status         : null,
    applicationKey : null
  },
  add : {
    showDialog : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null
  },
  destroy : {
    showDialog : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null
  },
  edit : {
    showDialog : false,
    isView     : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null
  },
  audit : {
    showDialog        : false,
    selected          : null,
    loading           : false,
    error             : null,
    result            : null,
    showChangesDialog : false,
    auditRecord       : null
  }
};

//defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_ADMIN_APPLICATIONS] : draft => {
    draft.isInitialized             = true;
    const preference                = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState          = preference;
    draft.grid.preferedColumnState  = preference;
    draft.destroy.error             = null;
    draft.destroy.result            = null;
  },
  [SET_GRID_COLUMNS] : ({grid, applications}: AdminApplicationsState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : applications.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_ADMIN_APPLICATIONS_GRID_COLUMNS] : ({grid}: AdminApplicationsState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [ADMIN_APPLICATIONS_GRID_REFRESHED] : ({add}: AdminApplicationsState) => {
    add.result = null;
  },
  [GET_ADMIN_APPLICATIONS_REQUEST] : ({applications}: AdminApplicationsState) => {
    applications.loading = true;
  },
  [GET_ADMIN_APPLICATIONS_RESPONSE] : ({applications}: AdminApplicationsState) => {
    applications.loading = false;
  },
  [GET_ADMIN_APPLICATIONS_ERROR] : ({applications}: AdminApplicationsState, {error}: AdminApplicationsAction) => {
    applications.loading = false;
    applications.error   = error;
  },
  [UPDATE_APPLICATIONS_SEARCH_QUERIES] : (
    {search}: AdminApplicationsState,
    {searchParams}: AdminApplicationsAction
  ) => {
    search.name = searchParams.name ?? null;
    search.status = searchParams.status ?? null;
    search.applicationKey = searchParams.applicationKey ?? null;
  },
  [RESET_APPLICATIONS_SEARCH_QUERIES] : ({search}: AdminApplicationsState) => {
    search.name = null;
    search.status = null;
    search.applicationKey = null;
  },
  [SHOW_ADD_APPLICATION_DIALOG] : ({add}: AdminApplicationsState, {showDialog}: AdminApplicationsAction) => {
    add.showDialog = showDialog;
  },
  [ADD_ADMIN_APPLICATION_REQUEST] : ({add}: AdminApplicationsState) => {
    add.loading = true;
    add.error = add.result = null;
  },
  [ADD_ADMIN_APPLICATION_RESPONSE] : ({add}: AdminApplicationsState, {applications}: AdminApplicationsAction) => {
    add.result = applications;
    add.selected = null;
    add.loading = add.showDialog = false;
  },
  [ADD_ADMIN_APPLICATION_ERROR] : ({add}: AdminApplicationsState, {error}: AdminApplicationsAction) => {
    add.error = error;
    add.result = null;
    add.loading = add.showDialog = false;
  },
  [DISPLAY_DELETE_APPLICATIONS_DIALOG] : (
    {destroy}: AdminApplicationsState,
    {applications, show}: AdminApplicationsAction
  ) => {
    destroy.selected = show ? applications : null;
    destroy.showDialog = show;
    destroy.result = null;
  },
  [DELETE_ADMIN_APPLICATIONS_REQUEST] : ({destroy}: AdminApplicationsState) => {
    destroy.loading = true;
    destroy.error = destroy.result = null;
  },
  [DELETE_ADMIN_APPLICATIONS_RESPONSE] : (
    {destroy}: AdminApplicationsState,
    {applications}: AdminApplicationsAction
  ) => {
    destroy.result = applications;
    destroy.selected = null;
    destroy.loading = destroy.showDialog = false;
  },
  [DELETE_ADMIN_APPLICATIONS_ERROR] : ({destroy}: AdminApplicationsState, {error}: AdminApplicationsAction) => {
    destroy.error = error;
    destroy.result = null;
    destroy.loading = destroy.showDialog = false;
  },
  [SHOW_EDIT_APPLICATION_DIALOG] : (
    {edit}: AdminApplicationsState,
    {showDialog, applications, isView}: AdminApplicationsAction
  ) => {
    edit.selected      = showDialog ? applications : null;
    edit.result        = null;
    edit.showDialog    = showDialog;
    edit.isView        = isView;
  },
  [EDIT_ADMIN_APPLICATION_REQUEST] : ({edit}: AdminApplicationsState) => {
    edit.loading = true;
    edit.error = edit.result = null;
  },
  [EDIT_ADMIN_APPLICATION_RESPONSE] : ({edit}: AdminApplicationsState, {applications}: AdminApplicationsAction) => {
    edit.result = applications;
    edit.selected = null;
    edit.loading = edit.showDialog = false;
  },
  [EDIT_ADMIN_APPLICATION_ERROR] : ({edit}: AdminApplicationsState, {error}: AdminApplicationsAction) => {
    edit.error = error;
    edit.result = edit.selected = null;
    edit.loading = edit.showDialog = false;
  },
  [SET_VIEW_SUB_GROUPS] : ({grid}: AdminApplicationsState, {viewSubGroups}) => {
    grid.includeSubGroups = viewSubGroups;
  },
  [AUDIT_ADMIN_APPLICATION] : ({audit}: AdminApplicationsState, {applications, show}: AdminApplicationsAction) => {
    audit.selected   = show ? applications : null;
    audit.showDialog = show;
  },
  [AUDIT_ADMIN_APPLICATION_SHOW_CHANGES_DIALOG] : ({audit}: AdminApplicationsState, {auditRecord, show}) => {
    audit.showChangesDialog = show;
    audit.auditRecord       = auditRecord;
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: AdminApplicationsState, {pageSize}) => {
    grid.pageSize = pageSize;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: AdminApplicationsState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
